import React, { useState } from 'react';
import { BaseModal } from '@/components/shared';
import type { MotivoExclusaoTipo } from '../../../models';
import { MOTIVO_EXCLUSAO_LABELS } from '../solicitacaoDetailsPage.definitions';
import { resolverSolicitacaoRelacionada } from './solicitacaoLink.utils';

export interface ExcluirSolicitacaoConfirmacao {
  motivoExclusaoTipo: MotivoExclusaoTipo;
  /** Só quando `motivoExclusaoTipo === 'outro'`. */
  motivoExclusao: string | null;
  /** Só quando `motivoExclusaoTipo === 'duplicidade'`. */
  solicitacaoRelacionadaId: string | null;
}

interface ExcluirSolicitacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** ID da própria Solicitação sendo excluída — usado só pra impedir vincular ela a si
   * mesma no campo "Solicitação duplicada". */
  idAtual: string;
  onConfirmar: (dados: ExcluirSolicitacaoConfirmacao) => void;
  confirmando?: boolean;
}

/**
 * Exclusão (soft-delete) — exclusiva do SI. Motivo é um dropdown fechado (não texto
 * livre como no Cancelamento) porque o objetivo aqui é justamente limpar solicitações
 * abertas por bug/erro operacional sem poluir dashboards/relatórios — motivo estruturado
 * é o que permite excluir esse ruído dos números de forma confiável.
 */
export default function ExcluirSolicitacaoModal({
  isOpen,
  onClose,
  idAtual,
  onConfirmar,
  confirmando,
}: ExcluirSolicitacaoModalProps) {
  const [motivoTipo, setMotivoTipo] = useState<MotivoExclusaoTipo | ''>('');
  const [motivoOutro, setMotivoOutro] = useState('');
  const [relacionadaInput, setRelacionadaInput] = useState('');
  const [erroRelacionada, setErroRelacionada] = useState<string | null>(null);
  const [verificando, setVerificando] = useState(false);

  const handleClose = () => {
    setMotivoTipo('');
    setMotivoOutro('');
    setRelacionadaInput('');
    setErroRelacionada(null);
    onClose();
  };

  const relacionadaValida = motivoTipo !== 'duplicidade' || relacionadaInput.trim().length > 0;
  const outroValido = motivoTipo !== 'outro' || motivoOutro.trim().length > 0;
  const podeConfirmar = motivoTipo !== '' && relacionadaValida && outroValido && !confirmando && !verificando;

  const handleConfirmar = async () => {
    if (!podeConfirmar) return;

    if (motivoTipo !== 'duplicidade') {
      onConfirmar({ motivoExclusaoTipo: motivoTipo, motivoExclusao: motivoOutro.trim() || null, solicitacaoRelacionadaId: null });
      return;
    }

    setVerificando(true);
    let resultado: Awaited<ReturnType<typeof resolverSolicitacaoRelacionada>>;
    try {
      resultado = await resolverSolicitacaoRelacionada(relacionadaInput, idAtual);
    } finally {
      setVerificando(false);
    }

    if ('erro' in resultado) {
      setErroRelacionada(resultado.erro);
      return;
    }
    setErroRelacionada(null);
    onConfirmar({ motivoExclusaoTipo: motivoTipo, motivoExclusao: null, solicitacaoRelacionadaId: resultado.id });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Excluir Solicitação"
      maxWidth="max-w-md"
      fullHeight={false}
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Voltar
          </button>
          <button type="button" className="btn-primary" disabled={!podeConfirmar} onClick={handleConfirmar}>
            {verificando ? 'Verificando...' : confirmando ? 'Excluindo...' : 'Confirmar Exclusão'}
          </button>
        </div>
      }
    >
      <div className="space-y-3 py-4">
        <p className="text-sm text-gray-600">
          A Solicitação não é apagada — ela sai das listas por padrão, mas fica registrada
          (histórico e auditoria continuam intactos).
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700">Motivo *</label>
          <select
            className="input-primary mt-1 w-full"
            value={motivoTipo}
            onChange={(event) => {
              setMotivoTipo(event.target.value as MotivoExclusaoTipo);
              setErroRelacionada(null);
            }}
            autoFocus
          >
            <option value="" disabled>
              Selecione um motivo
            </option>
            {Object.entries(MOTIVO_EXCLUSAO_LABELS).map(([valor, label]) => (
              <option key={valor} value={valor}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {motivoTipo === 'duplicidade' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Solicitação duplicada *</label>
            <input
              type="text"
              className="input-primary mt-1 w-full"
              placeholder="Cole o link (ou ID) da outra Solicitação"
              value={relacionadaInput}
              onChange={(event) => {
                setRelacionadaInput(event.target.value);
                if (erroRelacionada) setErroRelacionada(null);
              }}
            />
            {erroRelacionada && <p className="mt-1 text-xs text-red-600">{erroRelacionada}</p>}
          </div>
        )}

        {motivoTipo === 'outro' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Descreva o motivo *</label>
            <textarea
              className="input-primary mt-1 w-full"
              rows={3}
              value={motivoOutro}
              onChange={(event) => setMotivoOutro(event.target.value)}
            />
          </div>
        )}
      </div>
    </BaseModal>
  );
}
